import React, {useMemo, useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import "./ReactQuillStyles.css"; // Custom styles for ReactQuill
import { 
  Image, 
  Input, 
  Textarea, 
  Button, 
  Select, 
  SelectItem, 
  Card, 
  CardBody, 
  CardHeader,
  Chip,
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Badge,
  Tooltip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Progress
} from "@nextui-org/react";
import arrowBack from "../../Assets/blog/arrow-back.svg";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthContext } from "../../Context/AuthContext";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPlus,
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiCalendar,
  FiTag,
  FiUpload, 
  FiMenu,
  FiChevronRight,
  FiSave,
  FiGlobe,
  FiFileText,
  FiX,
  FiImage,
  FiSettings,
  // FiUploadCloud // Use FiUploadCloud instead of FiCloudUpload
} from "react-icons/fi";
import { RiDraftLine } from "react-icons/ri";
import { API_BASE_URL } from "../../config/apiConfig";

const API_BASE =  API_BASE_URL;



// Blog Service for Admin operations
const blogAdminService = {
  getAllBlogs: (token) => axios.get(`${API_BASE}/admin/blogs`, {
    headers: { Authorization: `Bearer ${token}` }
  }),
  
  createBlog: (blogData, token) => axios.post(
    `${API_BASE}/admin/blogs`,
    blogData,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  updateBlog: (id, blogData, token) => axios.put(
    `${API_BASE}/admin/blogs/${id}`,
    blogData,
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  deleteBlog: (id, token) => {
    //console.log('🗑️ Deleting blog:', { id, token: token ? 'Token present' : 'No token' });
    return axios.delete(
      `${API_BASE}/admin/blogs/${id}`,
      { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json'
        } 
      }
    );
  },
  
  // Publish a blog post (Admin only) - using the new publish endpoint
  publishBlog: (id, token) => axios.post(
    `${API_BASE}/admin/blogs/${id}/publish`,
    {},
    { headers: { Authorization: `Bearer ${token}` } }
  ),
  
  // Updated image upload to use Base64 encoding
 uploadImage: (imageData, token) => axios.post(
  `${API_BASE}/admin/blogs/upload-image`,
  imageData,
  { 
    headers: { 
      Authorization: `Bearer ${token}`,
      // Remove "Content-Type": "application/json" - let browser set it for FormData
    } 
  }
)
}

const AdminBlogManage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
const { token } = useContext(AuthContext);

  const isEditMode = Boolean(id);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [blogToDelete, setBlogToDelete] = useState(null);

  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [view, setView] = useState("list");
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [uploading, setUploading] = useState(false);

  // Image upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  
  // Category & Subcategory states
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedSubcategories, setSelectedSubcategories] = useState([]); // Array for multi-select
  const [availableSubcategories, setAvailableSubcategories] = useState([]);

  // Get user data from localStorage and check admin status
  const isAdmin = useMemo(() => {
    try {
      const userData = localStorage.getItem("user_data");
      if (!userData) return false;
      
      const parsed = JSON.parse(userData);
      const adminIds = [4, 83];
      const userId = parseInt(parsed?.id);
      return adminIds.includes(userId);
    } catch (error) {
      console.error('Error checking admin status:', error);
      return false;
    }
  }, []);

  // Image upload state

// Add this interceptor at the top of your AdminBlogManage component
useEffect(() => {
  // Add request interceptor to log all requests
  const requestInterceptor = axios.interceptors.request.use(request => {
    if (request.url.includes('/admin/blogs')) {
      // console.log('🚀 Admin API Request:', {
      //   url: request.url,
      //   method: request.method,
      //   headers: request.headers,
      //   data: request.data
      // });
    }
    return request;
  });

  // Add response interceptor to catch errors
  const responseInterceptor = axios.interceptors.response.use(
    response => response,
    error => {
      if (error.config?.url?.includes('/admin/blogs')) {
        console.error('❌ Admin API Error:', {
          url: error.config?.url,
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          headers: error.response?.headers
        });
        
        // Show the exact error message from server
        if (error.response?.data?.message) {
          toast.error(`Server says: ${error.response.data.message}`);
        }
      }
      return Promise.reject(error);
    }
  );

  // Cleanup interceptors
  return () => {
    axios.interceptors.request.eject(requestInterceptor);
    axios.interceptors.response.eject(responseInterceptor);
  };
}, []);


const modules = {
  toolbar: [
    // Text formatting
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }], // Text color & background
    [{ 'font': [] }], // Font family
    [{ 'size': ['small', false, 'large', 'huge'] }], // Font size
    
    // Alignment & spacing
    [{ 'align': [] }], // Left, center, right, justify
    [{ 'indent': '-1'}, { 'indent': '+1' }], // Indentation
    [{ 'direction': 'rtl' }], // Right-to-left text direction
    
    // Lists
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'list': 'check' }], // Checklist
    
    // Links & media
    ['link'],
    
    // Code & quotes
    ['blockquote', 'code-block'],
    
    // Advanced formatting
    [{ 'script': 'sub'}, { 'script': 'super' }], // Subscript/Superscript
    [{ 'header': 1 }, { 'header': 2 }], // Separate header buttons
    ['clean'], // Remove formatting
    
    // Custom separators for better organization
    [{ 'separator': '---' }],
  ],
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script', // For subscript/superscript
  'list', 'bullet', 'ordered', 'indent',
  'direction', // RTL support
  'link',
  'blockquote', 'code-block',
  'align',
  'clean' // Remove formatting
];



  // Categories for the blog
const categories = [
  { value: "Travel Tips", label: "Travel Tips", hexColor: "#4F46E5" },
  { value: "Travel", label: "Travel", hexColor: "#0EA5E9" },
  { value: "Projects", label: "Projects", hexColor: "#10B981" },
  { value: "Event", label: "Event", hexColor: "#F59E0B" },
  { value: "Sport", label: "Sport", hexColor: "#EF4444" },
  { value: "Music", label: "Music", hexColor: "#8B5CF6" },
  { value: "Art", label: "Art", hexColor: "#EC4899" },
  { value: "Wellness", label: "Wellness", hexColor: "#06B6D4" },
  { value: "Lifestyle", label: "Lifestyle", hexColor: "#14B8A6" },
  { value: "Productivity", label: "Productivity", hexColor: "#F97316" },
  { value: "Seasonal", label: "Seasonal", hexColor: "#D946EF" },
  { value: "Stories", label: "Stories", hexColor: "#8B5CF6" },
  { value: "Technology", label: "Technology", hexColor: "#3B82F6" },
  { value: "Food", label: "Food", hexColor: "#10B981" },
  { value: "Business", label: "Business", hexColor: "#0EA5E9" },
  { value: "Education", label: "Education", hexColor: "#8B5CF6" },
  { value: "Finance", label: "Finance", hexColor: "#10B981" },
  { value: "Health", label: "Health", hexColor: "#EF4444" },
  { value: "Photography", label: "Photography", hexColor: "#8B5CF6" },
  { value: "Books", label: "Books", hexColor: "#F59E0B" },
];

// Subcategories for each main category
const subcategoriesData = {
  "Travel Tips": [
    { value: "travel-tips-packing", label: "Packing Tips" },
    { value: "travel-tips-budget", label: "Budget Travel Tips" },
    { value: "travel-tips-safety", label: "Safety Tips" },
    { value: "travel-tips-planning", label: "Trip Planning" },
    { value: "travel-tips-accommodation", label: "Accommodation Tips" },
    { value: "travel-tips-transportation", label: "Transportation Tips" },
    { value: "travel-tips-food", label: "Food & Dining Tips" },
  ],
  "Travel": [
    { value: "travel-adventure", label: "Adventure Travel" },
    { value: "travel-budget", label: "Budget Travel" },
    { value: "travel-luxury", label: "Luxury Travel" },
    { value: "travel-solo", label: "Solo Travel" },
    { value: "travel-family", label: "Family Travel" },
    { value: "travel-backpacking", label: "Backpacking" },
    { value: "travel-road-trip", label: "Road Trips" },
    { value: "travel-beach", label: "Beach Destinations" },
    { value: "travel-mountain", label: "Mountain Getaways" },
    { value: "travel-cultural", label: "Cultural Immersion" },
    { value: "travel-food", label: "Food & Culinary Travel" },
    { value: "travel-wellness", label: "Wellness Retreats" },
  ],
  "Projects": [
    { value: "projects-diy", label: "DIY Projects" },
    { value: "projects-home-improvement", label: "Home Improvement" },
    { value: "projects-gardening", label: "Gardening Projects" },
    { value: "projects-crafts", label: "Crafts & Art Projects" },
    { value: "projects-tech", label: "Tech Projects" },
    { value: "projects-woodworking", label: "Woodworking" },
    { value: "projects-renovation", label: "Home Renovation" },
    { value: "projects-upcycling", label: "Upcycling Projects" },
  ],
  "Event": [
    { value: "event-weddings", label: "Weddings" },
    { value: "event-birthdays", label: "Birthdays" },
    { value: "event-Art", label: "Arts & Culture" },
    { value: "event-corporate", label: "Corporate" },
    { value: "event-conferences", label: "Conferences" },
    { value: "event-festivals", label: "Festivals" },
    { value: "event-Music & Nightlife", label: "Music & Nightlife" },
    { value: "event-sports", label: "Sports" },
    { value: "event-charity", label: "Charity" },
    { value: "event-exhibitions", label: "Exhibitions" },
    { value: "event-launches", label: "Product Launches" },
    { value: "event-fashion", label: "Fashion" },
    { value: "event-Family & Lifestyle", label: "Family & Lifestyle" },
  ],
  "Sport": [
    { value: "sport-football", label: "Football" },
    { value: "sport-basketball", label: "Basketball" },
    { value: "sport-tennis", label: "Tennis" },
    { value: "sport-golf", label: "Golf" },
    { value: "sport-cricket", label: "Cricket" },
    { value: "sport-rugby", label: "Rugby" },
    { value: "sport-swimming", label: "Swimming" },
    { value: "sport-running", label: "Running" },
    { value: "sport-cycling", label: "Cycling" },
    { value: "sport-yoga", label: "Yoga" },
    { value: "sport-gym", label: "Gym & Fitness" },
    { value: "sport-extreme", label: "Extreme Sports" },
    { value: "sport-esports", label: "eSports" },
  ],
  // "Music": [
  //   { value: "music-rock", label: "Rock" },
  //   { value: "music-pop", label: "Pop" },
  //   { value: "music-hiphop", label: "Hip Hop" },
  //   { value: "music-jazz", label: "Jazz" },
  //   { value: "music-classical", label: "Classical" },
  //   { value: "music-electronic", label: "Electronic" },
  //   { value: "music-rnb", label: "R&B" },
  //   { value: "music-country", label: "Country" },
  //   { value: "music-indie", label: "Indie" },
  //   { value: "music-metal", label: "Metal" },
  //   { value: "music-folk", label: "Folk" },
  //   { value: "music-reggae", label: "Reggae" },
  //   { value: "music-blues", label: "Blues" },
  //   { value: "music-instruments", label: "Musical Instruments" },
  //   { value: "music-production", label: "Music Production" },
  // ],
  // "Art": [
  //   { value: "art-painting", label: "Painting" },
  //   { value: "art-drawing", label: "Drawing" },
  //   { value: "art-sculpture", label: "Sculpture" },
  //   { value: "art-digital", label: "Digital Art" },
  //   { value: "art-photography", label: "Photography" },
  //   { value: "art-illustration", label: "Illustration" },
  //   { value: "art-graffiti", label: "Graffiti & Street Art" },
  //   { value: "art-ceramics", label: "Ceramics" },
  //   { value: "art-printmaking", label: "Printmaking" },
  //   { value: "art-collage", label: "Collage" },
  //   { value: "art-mixed-media", label: "Mixed Media" },
  //   { value: "art-architecture", label: "Architecture" },
  //   { value: "art-fashion", label: "Fashion Design" },
  //   { value: "art-graphic", label: "Graphic Design" },
  // ],
  "Wellness": [
    { value: "wellness-meditation", label: "Meditation" },
    { value: "wellness-yoga", label: "Yoga" },
    { value: "wellness-nutrition", label: "Nutrition" },
    { value: "wellness-fitness", label: "Fitness" },
    { value: "wellness-mental-health", label: "Mental Health" },
    { value: "wellness-sleep", label: "Sleep & Recovery" },
    { value: "wellness-holistic", label: "Holistic Health" },
    { value: "wellness-spa", label: "Spa & Relaxation" },
    { value: "wellness-ayurveda", label: "Ayurveda" },
    { value: "wellness-acupuncture", label: "Acupuncture" },
    { value: "wellness-massage", label: "Massage Therapy" },
    { value: "wellness-diet", label: "Diet & Detox" },
    { value: "wellness-mindfulness", label: "Mindfulness" },
  ],
  "Lifestyle": [
    { value: "lifestyle-fashion", label: "Fashion & Style" },
    { value: "lifestyle-beauty", label: "Beauty" },
    { value: "lifestyle-home", label: "Home & Decor" },
    { value: "lifestyle-relationships", label: "Relationships" },
    { value: "lifestyle-parenting", label: "Parenting" },
    { value: "lifestyle-pets", label: "Pets" },
    { value: "lifestyle-minimalism", label: "Minimalism" },
    { value: "lifestyle-sustainable", label: "Sustainable Living" },
    { value: "lifestyle-hobbies", label: "Hobbies" },
    { value: "lifestyle-entertainment", label: "Entertainment" },
    { value: "lifestyle-social", label: "Social Life" },
    { value: "lifestyle-personal-growth", label: "Personal Growth" },
  ],
  "Productivity": [
    { value: "productivity-time-management", label: "Time Management" },
    { value: "productivity-goal-setting", label: "Goal Setting" },
    { value: "productivity-habits", label: "Habit Building" },
    { value: "productivity-focus", label: "Focus & Concentration" },
    { value: "productivity-tools", label: "Productivity Tools" },
    { value: "productivity-work-life", label: "Work-Life Balance" },
    { value: "productivity-organization", label: "Organization" },
    { value: "productivity-planning", label: "Planning Systems" },
    { value: "productivity-motivation", label: "Motivation" },
    { value: "productivity-delegation", label: "Delegation" },
    { value: "productivity-remote-work", label: "Remote Work" },
    { value: "productivity-learning", label: "Learning Efficiency" },
  ],
  "Seasonal": [
    { value: "seasonal-christmas", label: "Christmas" },
    { value: "seasonal-halloween", label: "Halloween" },
    { value: "seasonal-easter", label: "Easter" },
    { value: "seasonal-new-year", label: "New Year" },
    { value: "seasonal-valentines", label: "Valentine's Day" },
    { value: "seasonal-thanksgiving", label: "Thanksgiving" },
    { value: "seasonal-summer", label: "Summer Activities" },
    { value: "seasonal-winter", label: "Winter Activities" },
    { value: "seasonal-spring", label: "Spring Activities" },
    { value: "seasonal-autumn", label: "Autumn Activities" },
    { value: "seasonal-holidays", label: "Holiday Planning" },
    { value: "seasonal-festivals", label: "Seasonal Festivals" },
  ],
  "Stories": [
    { value: "stories-personal", label: "Personal Stories" },
    { value: "stories-travel", label: "Travel Stories" },
    { value: "stories-fiction", label: "Fiction" },
    { value: "stories-inspirational", label: "Inspirational Stories" },
    { value: "stories-humorous", label: "Humorous Stories" },
    { value: "stories-mystery", label: "Mystery" },
    { value: "stories-romance", label: "Romance" },
    { value: "stories-adventure", label: "Adventure Stories" },
    { value: "stories-historical", label: "Historical Stories" },
    { value: "stories-fantasy", label: "Fantasy" },
    { value: "stories-sci-fi", label: "Science Fiction" },
    { value: "stories-interviews", label: "Interviews" },
  ],
  "Technology": [
    { value: "tech-ai", label: "Artificial Intelligence" },
    { value: "tech-web", label: "Web Development" },
    { value: "tech-mobile", label: "Mobile Development" },
    { value: "tech-gadgets", label: "Gadgets & Reviews" },
    { value: "tech-security", label: "Cybersecurity" },
    { value: "tech-software", label: "Software Engineering" },
    { value: "tech-blockchain", label: "Blockchain & Crypto" },
    { value: "tech-data-science", label: "Data Science" },
    { value: "tech-cloud", label: "Cloud Computing" },
    { value: "tech-iot", label: "Internet of Things" },
    { value: "tech-gaming", label: "Gaming" },
    { value: "tech-startups", label: "Startups & Innovation" },
    { value: "tech-programming", label: "Programming Languages" },
    { value: "tech-hardware", label: "Hardware" },
    { value: "tech-robotics", label: "Robotics" },
    { value: "tech-vr-ar", label: "VR & AR" },
  ],
  "Food": [
    { value: "food-recipes", label: "Recipes" },
    { value: "food-restaurants", label: "Restaurant Reviews" },
    { value: "food-healthy", label: "Healthy Eating" },
    { value: "food-baking", label: "Baking" },
    { value: "food-vegan", label: "Vegan & Vegetarian" },
    { value: "food-desserts", label: "Desserts" },
    { value: "food-drinks", label: "Drinks & Beverages" },
    { value: "food-cooking-tips", label: "Cooking Tips" },
    { value: "food-meal-prep", label: "Meal Prep" },
    { value: "food-international", label: "International Cuisine" },
    { value: "food-street-food", label: "Street Food" },
    { value: "food-fine-dining", label: "Fine Dining" },
    { value: "food-food-travel", label: "Food Travel" },
  ],
  "Business": [
    { value: "business-entrepreneurship", label: "Entrepreneurship" },
    { value: "business-marketing", label: "Marketing" },
    { value: "business-finance", label: "Finance" },
    { value: "business-management", label: "Management" },
    { value: "business-leadership", label: "Leadership" },
    { value: "business-sales", label: "Sales" },
    { value: "business-investing", label: "Investing" },
    { value: "business-strategy", label: "Business Strategy" },
    { value: "business-startups", label: "Startups" },
    { value: "business-ecommerce", label: "E-commerce" },
  ],
  "Education": [
    { value: "education-learning", label: "Learning Techniques" },
    { value: "education-online", label: "Online Education" },
    { value: "education-career", label: "Career Development" },
    { value: "education-languages", label: "Language Learning" },
    { value: "education-skill-development", label: "Skill Development" },
    { value: "education-study-tips", label: "Study Tips" },
    { value: "education-parenting", label: "Education & Parenting" },
  ],
  "Finance": [
    { value: "finance-investing", label: "Investing" },
    { value: "finance-saving", label: "Saving Money" },
    { value: "finance-budgeting", label: "Budgeting" },
    { value: "finance-stocks", label: "Stock Market" },
    { value: "finance-crypto", label: "Cryptocurrency" },
    { value: "finance-real-estate", label: "Real Estate" },
    { value: "finance-taxes", label: "Tax Planning" },
  ],
  "Health": [
    { value: "health-fitness", label: "Fitness" },
    { value: "health-nutrition", label: "Nutrition" },
    { value: "health-mental", label: "Mental Health" },
    { value: "health-chronic", label: "Chronic Conditions" },
    { value: "health-preventive", label: "Preventive Care" },
    { value: "health-alternative", label: "Alternative Medicine" },
  ],
  "Photography": [
    { value: "photography-portrait", label: "Portrait Photography" },
    { value: "photography-landscape", label: "Landscape Photography" },
    { value: "photography-street", label: "Street Photography" },
    { value: "photography-wildlife", label: "Wildlife Photography" },
    { value: "photography-editing", label: "Photo Editing" },
    { value: "photography-equipment", label: "Camera Equipment" },
  ],
  "Books": [
    { value: "books-fiction", label: "Fiction" },
    { value: "books-nonfiction", label: "Non-Fiction" },
    { value: "books-self-help", label: "Self-Help" },
    { value: "books-reviews", label: "Book Reviews" },
    { value: "books-recommendations", label: "Recommendations" },
    { value: "books-writing", label: "Writing Tips" },
  ],
};

// Helper function to get subcategories for a category
const getSubcategoriesForCategory = (categoryValue) => {
  const category = categories.find(cat => cat.value === categoryValue);
  return category?.subcategories || subcategoriesData[categoryValue] || [];
};


const getCategoryColor = (category) => {
  const found = categories.find(cat => cat.value === category);
  return found?.color || "default";
};

const getCategoryHexColor = (category) => {
  const found = categories.find(cat => cat.value === category);
  return found?.hexColor || "#6B7280"; // Default gray
};


//   // Check if user is admin
// const isAdminUser = () => {
//   try {
//     const userData = localStorage.getItem("user_data");
//     if (userData) {
//       const parsed = JSON.parse(userData);
//       console.log('👤 User data:', parsed);
      
//       // Backend only allows users with ID 4 or 83
//       const adminIds = [4, 83];
//       const userId = parseInt(parsed?.id);
      
//       const isAdmin = adminIds.includes(userId);
//       console.log('✅ Is admin:', isAdmin, '(user ID:', userId, ')');
      
//       return isAdmin;
//     }
//     console.log('❌ No user data found in localStorage');
//     return false;
//   } catch (e) {
//     console.error('❌ Error checking admin status:', e);
//     return false;
//   }
// };

  // Form for create/edit
  const {
    control: controlForm,
    handleSubmit: handleSubmitForm,
    reset: resetForm,
    setValue,
    getValues, 
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      title: "",
      excerpt: "",
      content: "",
      category: "",
      subcategory: [], // Changed to array for multi-select
      blog_image: "",
      is_published: true,
    },
  });

const handleImageUpload = async (file) => {
  setUploading(true);
  setUploadError('');
  
  try {
    const formData = new FormData();
    formData.append('image', file);
    
    // ✅ Use token directly from context
    if (!token) {
      throw new Error('No authentication token found');
    }
    
    const response = await axios.post(
      `${API_BASE_URL}/admin/blogs/upload-image`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        },
        timeout: 30000,
      }
    );
    
    if (response.data.success && response.data.url) {
      return response.data.url;
    } else {
      throw new Error(response.data.message || 'Upload failed');
    }
    
  } catch (error) {
    console.error('Upload error:', error);
    
    let errorMsg = 'Failed to upload image';
    if (error.response?.data?.message) {
      errorMsg = error.response.data.message;
    } else if (error.response?.data?.errors) {
      errorMsg = Object.values(error.response.data.errors).flat()[0];
    }
    
    setUploadError(errorMsg);
    toast.error(errorMsg);
    throw error;
  } finally {
    setUploading(false);
  }
};

  // Fetch all blogs
  const fetchBlogs = async () => {
    setLoading(true);
    try {
      //console.log('📡 Fetching blogs...');
      const response = await blogAdminService.getAllBlogs(token);
      //console.log('📥 Blogs API response:', response.data);
      
      // Handle different API response structures
      let allBlogs = [];
      if (Array.isArray(response.data)) {
        allBlogs = response.data;
        //console.log('ℹ️ Response is an array with', allBlogs.length, 'blogs');
      } else if (response.data.data && Array.isArray(response.data.data)) {
        allBlogs = response.data.data;
      } else if (response.data.blogs && Array.isArray(response.data.blogs)) {
        allBlogs = response.data.blogs;
      } else {
        console.warn('⚠️ Unexpected response format:', response.data);
        allBlogs = [];
      }
      
      //console.log(`✅ Loaded ${allBlogs.length} blogs`);
      setBlogs(allBlogs);
    } catch (error) {
      console.error("❌ Error fetching blogs:", error);
      console.error("❌ Error response:", error.response?.data);
      toast.error("Failed to load blogs");
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  const convertToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

 useEffect(() => {
  // Check token first
  if (!token) {
    toast.error("Please login first");
    navigate("/login");
    return;
  }

  // Then check admin status
  if (!isAdmin) {
    console.log('🚫 AdminBlogManage: Access denied - not admin');
    toast.error("Access denied. Admin only.");
    navigate("/blogs");
    return;
  }

  // If both checks pass, fetch blogs
  fetchBlogs();
}, [token, isAdmin, navigate]);

  // Load blog data for edit mode
  useEffect(() => {
    if (isEditMode && blogs.length > 0) {
      const blog = blogs.find(b => String(b.id) === String(id));
      if (blog) {
        setSelectedBlog(blog);
        
        // Handle categories array - assuming API returns array like ["MainCategory", "Subcategory"]
        const blogCategories = blog.categories || [];
        const mainCategory = Array.isArray(blogCategories) ? blogCategories[0] || "" : blog.category || "";
        // For multiple subcategories, take all items after the first one (main category)
        const subCategories = Array.isArray(blogCategories) ? blogCategories.slice(1) : [];
        
        setSelectedCategory(mainCategory);
        if (mainCategory) {
          const subs = getSubcategoriesForCategory(mainCategory);
          setAvailableSubcategories(subs);
          setSelectedSubcategories(subCategories); // Set array of subcategories
        }
        
        setValue("title", blog.title || "");
        setValue("excerpt", blog.excerpt || "");
        setValue("content", blog.content || "");
        setValue("category", mainCategory);
        setValue("subcategory", subCategories); // Set array of subcategories
        setValue("blog_image", blog.image || blog.featured_image || "");
        setValue("is_published", blog.is_published ?? true);
        if (blog.image || blog.featured_image) {
          setImagePreview(blog.image || blog.featured_image);
        }
        setView("edit");
      }
    }
  }, [isEditMode, id, blogs, setValue]);

  // Filter blogs based on search
  const filteredBlogs = blogs.filter(blog => 
    blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.category.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle create/update
const onSubmit = async (data) => {
  setSubmitting(true);
  try {
    console.log('✅ Is admin:', isAdmin);
    
    const categoryValue = data.category || selectedCategory || "";
    
    const categoriesArray = [categoryValue];
    if (selectedSubcategories && selectedSubcategories.length > 0) {
      categoriesArray.push(...selectedSubcategories);
    }
    
    const blogData = {
      title: data.title,
      excerpt: data.excerpt,
      content: data.content,
      categories: categoriesArray,
      category: categoryValue,
      image: data.blog_image,
      is_published: data.is_published,
    };

    if (isEditMode && selectedBlog) {
      await blogAdminService.updateBlog(selectedBlog.id, blogData, token);
      toast.success("✅ Blog updated successfully!");
    } else {
      const response = await blogAdminService.createBlog(blogData, token);
      toast.success("✨ Blog created successfully!");
    }

    setView("list");
    fetchBlogs();
    resetForm();
    navigate("/admin/blog");
  } catch (error) {
    console.error("❌ Error saving blog:", error);
    toast.error(error.response?.data?.message || "Failed to save blog");
  } finally {
    setSubmitting(false);
  }
};



  // Handle delete confirmation
  const confirmDelete = (blog) => {
    setBlogToDelete(blog);
    onOpen();
  };

  // Handle delete
  const handleDelete = async () => {
    if (!blogToDelete) return;

    try {
      //console.log('🚀 Starting delete request for blog:', blogToDelete.id);
      //console.log('📡 Using token:', token ? 'Token available' : 'NO TOKEN');
      
      const response = await blogAdminService.deleteBlog(blogToDelete.id, token);
      //console.log('✅ Delete response:', response.data);
      
      toast.success("🗑️ Blog deleted successfully!");
      
      // Optimistically remove from local state first
      const deletedId = blogToDelete.id;
      const updatedBlogs = blogs.filter(blog => blog.id !== deletedId);
      setBlogs(updatedBlogs);
      //console.log(`📝 Removed blog ${deletedId} from local state, ${updatedBlogs.length} blogs remaining`);
      
      // Then fetch fresh data from server
      //console.log('🔄 Fetching fresh blog list...');
      await fetchBlogs();
      
      // Verify the refresh worked
      setTimeout(() => {
        //console.log('🔍 Current blogs state:', blogs.length, 'blogs');
        if (blogs.find(b => b.id === deletedId)) {
          console.error('❌ Blog still in state after delete!');
          fetchBlogs(); // Try again
        }
      }, 500);
      
    } catch (error) {
      console.error('❌ Delete error:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      
      // Handle different error types
      if (error.response) {
        if (error.response.status === 401) {
          toast.error("❌ Unauthorized: Your session has expired. Please login again.");
        } else if (error.response.status === 403) {
          toast.error("🚫 Forbidden: You don't have permission to delete this blog.");
        } else if (error.response.status === 404) {
          toast.error("🔍 Blog not found. It may have already been deleted.");
        } else {
          toast.error(error.response.data?.message || `Server error: ${error.response.status}`);
        }
      } else if (error.request) {
        toast.error("🌐 Network error: Please check your connection and try again.");
      } else {
        toast.error("❌ Failed to delete blog: " + (error.message || "Unknown error"));
      }
    } finally {
      onClose();
      setBlogToDelete(null);
    }
  };

  // Handle publish
  const handlePublish = async (blog) => {
    try {
      const response = await blogAdminService.publishBlog(blog.id, token);
      toast.success(response.data.message || "Blog published successfully!");
      fetchBlogs();
    } catch (error) {
      console.error("Error publishing blog:", error);
      toast.error(error?.response?.data?.message || "Failed to publish blog");
    }
  };

  // Navigate to edit page
  const handleEdit = (blog) => {
    navigate(`/admin/blog/edit/${blog.id}`);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Stats
  const stats = {
    total: blogs.length,
    published: blogs.filter(b => b.is_published).length,
    drafts: blogs.filter(b => !b.is_published).length,
  };

// Replace both handleFileChange and onFileChange with this:
const handleFileChange = async (e) => {
  const file = e.target.files[0];
  if (!file) return;

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/jpg'];
  if (!validTypes.includes(file.type)) {
    toast.error("Please select a valid image file (JPEG, PNG, GIF, or WebP)");
    return;
  }

  // Validate file size (max 2MB based on API)
  const maxSize = 2 * 1024 * 1024; // 2MB
  if (file.size > maxSize) {
    toast.error("Image size must be less than 2MB");
    return;
  }

  // Set selected file and create preview
  setSelectedFile(file);
  setUploadError(null);

  // Create preview
  const reader = new FileReader();
  reader.onloadend = () => {
    setImagePreview(reader.result);
  };
  reader.readAsDataURL(file);

  // Upload immediately (or you can use a separate upload button)
  try {
    setIsUploading(true);
    setUploadProgress(30);
    
    const imageUrl = await handleImageUpload(file);
    
    setUploadProgress(100);
    toast.success("Image uploaded successfully!");
    
    // Set the uploaded URL to the form
    setValue('blog_image', imageUrl, { shouldValidate: true });
    
    // Optional: Clear the preview and use the actual uploaded image
    setImagePreview(imageUrl);
    
  } catch (error) {
    console.error('Failed to upload image:', error);
    // Error is already handled in handleImageUpload
  } finally {
    setIsUploading(false);
    setTimeout(() => setUploadProgress(0), 1000);
  }
};

  // Remove selected/uploaded image
  const removeImage = () => {
    setSelectedFile(null);
    setImagePreview(null);
    setUploadProgress(0);
    setUploadError(null);
    setValue('blog_image', '');
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Trigger file input click 
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Render list view
  const renderListView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Event Management</h1>
          <p className="mt-1 text-gray-600">Create, edit, and manage your events</p>
        </div>
        <div className="flex gap-3">
          {stats.drafts > 0 && (
            <Button
              variant="flat"
              color="warning"
              onClick={() => navigate("/admin/blog/drafts")}
              startContent={<FiFileText className="text-lg" />}
              className="bg-amber-100 text-amber-700 hover:bg-amber-200"
            >
              View {stats.drafts} Drafts
            </Button>
          )}
          <Button
            color="primary"
            onClick={() => {
              navigate("/admin/blog/create");
              setView("create");
            }}
            className="text-white transition-shadow shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-xl"
            startContent={<FiPlus className="text-lg" />}
          >
            Create New Post
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">Total Posts</p>
                <h3 className="mt-1 text-2xl font-bold text-blue-900">{stats.total}</h3>
              </div>
              <div className="p-3 rounded-full bg-blue-500/20">
                <FiFileText className="text-2xl text-blue-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="border border-green-200 bg-gradient-to-br from-green-50 to-green-100">
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-800">Published</p>
                <h3 className="mt-1 text-2xl font-bold text-green-900">{stats.published}</h3>
              </div>
              <div className="p-3 rounded-full bg-green-500/20">
                <FiGlobe className="text-2xl text-green-600" />
              </div>
            </div>
          </CardBody>
        </Card>

        <Card className="border bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardBody className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-amber-800">Drafts</p>
                <h3 className="mt-1 text-2xl font-bold text-amber-900">{stats.drafts}</h3>
              </div>
              <div className="p-3 rounded-full bg-amber-500/20">
                <RiDraftLine className="text-2xl text-amber-600" />
              </div>
            </div>
          </CardBody>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <div className="flex flex-col gap-4 md:flex-row">
            <Input
              placeholder="Search blogs by title, excerpt, or category..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              startContent={<FiMenu className="text-gray-400" />}
              className="flex-1"
              radius="lg"
              variant="bordered"
            />
            <Select
              placeholder="Filter by status"
              className="w-full md:w-40"
              radius="lg"
              variant="bordered"
            >
              <SelectItem key="all" value="all">All Status</SelectItem>
              <SelectItem key="published" value="published">Published</SelectItem>
              <SelectItem key="draft" value="draft">Drafts</SelectItem>
            </Select>
          </div>
        </CardBody>
      </Card>

      {/* Blogs List */}
      {loading ? (
        <div className="py-12 text-center">
          <div className="w-16 h-16 mx-auto border-t-2 border-b-2 rounded-full animate-spin border-mainGradiant"></div>
          <p className="mt-4 text-gray-600">Loading your blog posts...</p>
        </div>
      ) : filteredBlogs.length === 0 ? (
        <Card className="border-2 border-gray-300 border-dashed">
          <CardBody className="py-12 text-center">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
              <FiFileText className="text-2xl text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-700">No blog posts found</h3>
            <p className="mb-6 text-gray-500">
              {searchTerm ? "No results match your search. Try different keywords." : "Get started by creating your first blog post."}
            </p>
            <Button
              color="primary"
              onClick={() => {
                navigate("/admin/blog/create");
                setView("create");
              }}
              className="bg-gradient-to-r from-blue-600 to-purple-600"
              startContent={<FiPlus />}
            >
              Create Your First Post
            </Button>
          </CardBody>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredBlogs.map((blog) => (
            <Card 
              key={blog.id} 
              className="transition-all duration-300 border border-gray-200 hover:shadow-lg hover:-translate-y-1"
            >
              <CardBody className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Blog Image */}
                  {blog.featured_image && (
                    <div className="relative h-48 md:w-48 md:h-auto">
                      <Image
                        src={blog.featured_image}
                        alt={blog.title}
                        className="object-cover w-full h-full rounded-l-lg"
                        removeWrapper
                      />
                      <div className="absolute top-3 left-3">
                        <Badge 
                          color={blog.is_published ? "success" : "warning"}
                          variant="flat"
                          className="backdrop-blur-sm bg-white/70"
                        >
                          {blog.is_published ? (
                            <>
                              <FiCheckCircle className="mr-1" /> Published
                            </>
                          ) : (
                            <>
                              <RiDraftLine className="mr-1" /> Draft
                            </>
                          )}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {/* Blog Content */}
                  <div className="flex-1 p-6">
                    <div className="flex flex-col h-full">
                      <div className="flex-1">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="text-xl font-bold text-gray-900 line-clamp-1">
                              {blog.title}
                            </h3>
                            <div className="flex items-center gap-2 mt-2">
                            <Chip 
                                style={{
                                  backgroundColor: getCategoryHexColor(blog.category) + '20',
                                  color: getCategoryHexColor(blog.category),
                                  borderColor: getCategoryHexColor(blog.category) + '40'
                                }}
                                variant="flat"
                                size="sm"
                                startContent={
                                  <div 
                                    className="w-2 h-2 mr-1 rounded-full"
                                    style={{ backgroundColor: getCategoryHexColor(blog.category) }}
                                  />
                                }
                              >
                                {blog.category}
                              </Chip>
                              <div className="flex items-center text-sm text-gray-500">
                                <FiCalendar className="mr-1" />
                                {formatDate(blog.created_at)}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <p className="mb-4 text-gray-600 line-clamp-2">
                          {blog.excerpt}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2">
                          <Badge 
                            color="default" 
                            variant="flat"
                            className="text-gray-700 bg-gray-100"
                          >
                            {blog.comments_count || 0} comments
                          </Badge>
                          <Badge 
                            color="default" 
                            variant="flat"
                            className="text-gray-700 bg-gray-100"
                          >
                            {blog.likes_count || 0} likes
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Tooltip content="Preview" color="foreground">
                            <Button
                              isIconOnly
                              size="sm"
                              variant="flat"
                              onClick={() => window.open(`/blogs/${blog.slug}`, '_blank')}
                              className="text-gray-600"
                            >
                              <FiEye />
                            </Button>
                          </Tooltip>
                          
                          <Tooltip content="Edit" color="warning">
                            <Button
                              isIconOnly
                              size="sm"
                              color="warning"
                              variant="flat"
                              onClick={() => handleEdit(blog)}
                            >
                              <FiEdit2 />
                            </Button>
                          </Tooltip>
                          
                          <Tooltip content="Delete" color="danger">
                            <Button
                              isIconOnly
                              size="sm"
                              color="danger"
                              variant="flat"
                              onClick={() => confirmDelete(blog)}
                            >
                              <FiTrash2 />
                            </Button>
                          </Tooltip>

                          
                          <Button
                            size="sm"
                            variant="flat"
                            endContent={<FiChevronRight />}
                            onClick={() => navigate(`/blogs/${blog.slug}`)}
                          >
                            View Post
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  // Render form view
  const renderFormView = () => (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          isIconOnly
          variant="flat"
          onClick={() => {
            setView("list");
            navigate("/admin/blog");
          }}
          className="rounded-full"
        >
          <FiArrowLeft />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEditMode ? "Edit Blog Post" : "Create New Blog Post"}
          </h1>
          <p className="mt-1 text-gray-600">
            {isEditMode ? "Update your existing blog post" : "Share your thoughts with the world"}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmitForm(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main Form */}
          <div className="space-y-6 lg:col-span-2">
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h3 className="text-lg font-semibold">Post Content</h3>
              </CardHeader>
              <CardBody className="pt-4 space-y-6">
                {/* Title */}
                <Controller
                  name="title"
                  control={controlForm}
                  rules={{
                    required: { value: true, message: "Title is required" },
                    minLength: { value: 3, message: "Title must be at least 3 characters" },
                  }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      label="Post Title"
                      minRows={1}
                      placeholder="Catchy title that grabs attention"
                      isInvalid={!!errors.title}
                      errorMessage={errors.title?.message}
                      variant="bordered"
                      radius="lg"
                      size="lg"
                      classNames={{
                        input: "text-lg boder-none"
                      }}
                    />
                  )}
                />

                {/* Excerpt */}
                <Controller
                  name="excerpt"
                  control={controlForm}
                  rules={{
                    required: { value: true, message: "Excerpt is required" },
                    maxLength: { value: 1000, message: "Maximum 1000 characters" },
                  }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      label="Excerpt"
                      placeholder="Brief summary of your post (appears in blog listings)"
                      minRows={4}
                      maxLength={1000}
                      classNames={{
                            input: "border-none",
                          }}
                      isInvalid={!!errors.excerpt}
                      errorMessage={errors.excerpt?.message}
                      variant="bordered"
                      radius="lg"
                      description={`${field.value.length}/1000 characters`}
                    />
                  )}
                />

                {/* Content */}
                <Controller
                  name="content"
                  control={controlForm}
                  rules={{ required: { value: true, message: "Content is required" } }}
                  render={({ field }) => (
                    <div>
                      <label className="block mb-2 text-sm font-medium text-gray-700">
                        Post Content <span className="text-red-500">*</span>
                      </label>
                      <ReactQuill
                        theme="snow"
                        value={field.value || ''}
                        onChange={field.onChange}
                        modules={modules}
                        formats={formats}
                        className="rounded-lg border border-default-200 min-h-[300px] mb-12"
                      />
                      {errors.content && (
                        <p className="mt-2 text-sm text-danger">{errors.content.message}</p>
                      )}
                      <p className="mt-8 text-xs text-gray-500">
                        Full rich text editor with headers, lists, formatting, and more.
                      </p>
                    </div>
                  )}
                />
              </CardBody>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Publishing */}
            <Card className="shadow-sm">
              <CardHeader className="pb-0">
                <h3 className="text-lg font-semibold">Publishing</h3>
              </CardHeader>
              <CardBody className="pt-4 space-y-4">
                {/* Category */}
                <Controller
                  name="category"
                  control={controlForm}
                  rules={{ required: { value: true, message: "Category is required" } }}
                  render={({ field }) => {
                    const handleCategoryChange = (keys) => {
                      const selected = Array.from(keys)[0];
                      setSelectedCategory(selected || "");
                      field.onChange(selected || "");
                      
                      // Update available subcategories when category changes
                      if (selected) {
                        const subs = getSubcategoriesForCategory(selected);
                        setAvailableSubcategories(subs);
                        setSelectedSubcategories([]); // Reset subcategories (array)
                        setValue("subcategory", []); // Clear form field (array)
                      } else {
                        setAvailableSubcategories([]);
                        setSelectedSubcategories([]); // Reset to empty array
                      }
                    };

                    return (
                      <Select
                        {...field}
                        label="Main Category"
                        placeholder="Select a category"
                        selectedKeys={selectedCategory ? new Set([selectedCategory]) : new Set()}
                        onSelectionChange={handleCategoryChange}
                        isInvalid={!!errors.category}
                        errorMessage={errors.category?.message}
                        variant="bordered"
                        radius="lg"
                        renderValue={(items) => {
                          if (!items || items.length === 0) return null;
                          const selectedValue = items[0].key;
                          const categoryData = categories.find(cat => cat.value === selectedValue);
                          
                          return (
                            <div className="flex items-center gap-2">
                              {categoryData && (
                                <div 
                                  className="flex-shrink-0 w-3 h-3 rounded-full"
                                  style={{ backgroundColor: categoryData.hexColor }}
                                />
                              )}
                              <span>{selectedValue}</span>
                              {availableSubcategories.length > 0 && (
                                <Chip size="sm" variant="flat" color="primary" className="ml-2">
                                  {availableSubcategories.length} subcategories
                                </Chip>
                              )}
                            </div>
                          );
                        }}
                      >
                        {categories.map((cat) => (
                          <SelectItem 
                            key={cat.value} 
                            value={cat.value}
                            textValue={cat.label}
                          >
                            <div className="flex items-center justify-between w-full">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="flex-shrink-0 w-3 h-3 rounded-full"
                                  style={{ backgroundColor: cat.hexColor }}
                                />
                                <span>{cat.label}</span>
                              </div>
                              {getSubcategoriesForCategory(cat.value).length > 0 && (
                                <span className="text-xs text-gray-500">
                                  {getSubcategoriesForCategory(cat.value).length} sub
                                </span>
                              )}
                            </div>
                          </SelectItem>
                        ))}
                      </Select>
                    );
                  }}
                />

                {/* Subcategory Selection (multiple selection) */}
                {availableSubcategories.length > 0 && (
                  <Controller
                    name="subcategory"
                    control={controlForm}
                    render={({ field }) => (
                      <Select
                        {...field}
                        label="Subcategories (Optional)"
                        placeholder="Select subcategories"
                        selectionMode="multiple"
                        selectedKeys={new Set(selectedSubcategories)}
                        onSelectionChange={(keys) => {
                          const selected = Array.from(keys);
                          setSelectedSubcategories(selected);
                          field.onChange(selected);
                        }}
                        variant="bordered"
                        radius="lg"
                      >
                        {availableSubcategories.map((sub) => (
                          <SelectItem 
                            key={sub.value} 
                            value={sub.value}
                            textValue={sub.label}
                          >
                            {sub.label}
                          </SelectItem>
                        ))}
                      </Select>
                    )}
                  />
                )}

                {/* Display Selected Categories */}
                {(selectedCategory || selectedSubcategories.length > 0) && (
                  <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                    <p className="mb-2 text-sm font-medium text-gray-700">Selected Categories:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedCategory && (
                        <div 
                          className="flex items-center gap-1 px-3 py-1 text-sm rounded-full"
                          style={{ 
                            backgroundColor: getCategoryHexColor(selectedCategory) + '20',
                            color: getCategoryHexColor(selectedCategory)
                          }}
                        >
                          <div 
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: getCategoryHexColor(selectedCategory) }}
                          />
                          <span>{selectedCategory}</span>
                        </div>
                      )}
                      {selectedSubcategories.map((sub) => (
                        <div key={sub} className="flex items-center gap-1 px-3 py-1 text-sm text-purple-800 bg-purple-100 rounded-full">
                          <div className="w-2 h-2 bg-purple-400 rounded-full" />
                          <span>{availableSubcategories.find(s => s.value === sub)?.label || sub}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

{/* Featured Image Upload */}
<div className="space-y-3">
  <label className="block text-sm font-medium text-gray-700">
    Featured Image
  </label>
  
  {/* Hidden file input */}
  <input
    type="file"
    ref={fileInputRef}
    onChange={handleFileChange}
    accept="image/*"
    className="hidden border-none"
  />
  
  {/* Image Preview/Upload Area */}
  <div className="space-y-3">
    {/* Current Image or Upload Area */}
    {imagePreview || watch('blog_image') ? (
      <div className="relative group">
        <Image
          src={imagePreview || watch('blog_image')}
          alt="Featured preview"
          className="object-cover w-full h-48 rounded-lg"
          removeWrapper
        />
        <div className="absolute inset-0 flex items-center justify-center transition-all duration-300 bg-black bg-opacity-0 rounded-lg opacity-0 group-hover:bg-opacity-40 group-hover:opacity-100">
          <div className="flex gap-2">
            <Button
              isIconOnly
              size="sm"
              variant="flat"
              color="warning"
              onClick={triggerFileInput}
              className="bg-white/80"
            >
              <FiEdit2 />
            </Button>
            <Button
              isIconOnly
              size="sm"
              variant="flat"
              color="danger"
              onClick={removeImage}
              className="bg-white/80"
            >
              <FiX />
            </Button>
          </div>
        </div>
      </div>
    ) : (
      <div
        onClick={triggerFileInput}
        className="p-6 text-center transition-all duration-300 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50"
      >
        <div className="flex items-center justify-center w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full">
          <FiUpload className="text-2xl text-blue-600" />
        </div>
        <p className="mb-1 text-sm text-gray-600">
          Click to upload or drag and drop
        </p>
        <p className="text-xs text-gray-500">
          PNG, JPG, GIF up to 5MB
        </p>
      </div>
    )}
    
    {/* Upload Progress */}
    {isUploading && (
      <div className="space-y-2">
        <Progress
          value={uploadProgress}
          color="primary"
          size="sm"
          classNames={{
            track: "bg-gray-200",
            indicator: "bg-gradient-to-r from-blue-500 to-purple-500"
          }}
        />
        <p className="text-xs text-center text-gray-600">
          Uploading... {uploadProgress}%
        </p>
      </div>
    )}
    
    {/* Upload Error */}
    {uploadError && (
      <div className="p-3 border border-red-200 rounded-lg bg-red-50">
        <p className="flex items-center gap-2 text-sm text-red-700">
          <FiXCircle className="flex-shrink-0" />
          {uploadError}
        </p>
      </div>
    )}
    
    
{/* Instead of a separate upload button, we're uploading immediately on file selection */}
{selectedFile && !isUploading && !watch('blog_image') && (
  <div className="p-3 mt-2 rounded-lg bg-blue-50">
    <p className="text-sm text-blue-700">
      Image selected. Upload in progress...
    </p>
  </div>
)}
    
    {/* Or use direct URL */}
    <div className="pt-2">
      <Controller
        name="blog_image"
        control={controlForm}
        render={({ field }) => (
          <Input
            {...field}
            placeholder="Or paste image URL here..."
            variant="bordered"
            radius="lg"
            size="sm"
             classNames={{
                            input: "border-none",
                          }}
            startContent={<FiImage className="text-gray-400" />}
            endContent={
              field.value && (
                <button
                  type="button"
                  onClick={() => {
                    field.onChange('');
                    setSelectedFile(null);
                    setImagePreview(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <FiX />
                </button>
              )
            }
          />
        )}
      />
    </div>
  </div>
</div>

                {/* Published Status */}
                <Controller
                  name="is_published"
                  control={controlForm}
                  render={({ field }) => (
                    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                      <div>
                        <p className="font-medium text-gray-900">Publish Status</p>
                        <p className="text-sm text-gray-500">
                          {field.value ? "Post will be visible to everyone" : "Save as draft"}
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                          className="border-none sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  )}
                />

                {/* Submit Button */}
                <Button
                  type="submit"
                  color="primary"
                  isLoading={submitting}
                  className="w-full text-white shadow-lg bg-gradient-to-r from-blue-600 to-purple-600 hover:shadow-xl"
                  startContent={submitting ? null : <FiSave />}
                  size="lg"
                >
                  {isEditMode ? "Update Post" : "Publish Post"}
                </Button>
              </CardBody>
            </Card>

            {/* Tips Card */}
            <Card className="border border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
              <CardBody className="p-4">
                <h4 className="flex items-center gap-2 mb-2 font-semibold text-blue-900">
                  <FiCheckCircle className="text-blue-600" />
                  Writing Tips
                </h4>
                <ul className="space-y-2 text-sm text-blue-800">
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                    Use a clear, descriptive title
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                    Add high-quality images
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                    Break content into sections
                  </li>
                  <li className="flex items-start gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5"></div>
                    Include relevant categories
                  </li>
                </ul>
              </CardBody>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );

  // Delete Confirmation Modal
  const renderDeleteModal = () => (
    <Modal isOpen={isOpen} onClose={onClose} backdrop="blur">
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-full">
                  <FiTrash2 className="text-xl text-red-600" />
                </div>
                <span>Delete Blog Post</span>
              </div>
            </ModalHeader>
            <ModalBody>
              <p className="text-gray-600">
                Are you sure you want to delete <span className="font-semibold text-gray-900">"{blogToDelete?.title}"</span>?
                This action cannot be undone.
              </p>
              <div className="p-4 mt-4 border border-red-200 rounded-lg bg-red-50">
                <p className="flex items-center gap-2 text-sm text-red-700">
                  <FiXCircle className="flex-shrink-0" />
                  All comments, likes, and data associated with this post will be permanently removed.
                </p>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="flat" onClick={onClose}>
                Cancel
              </Button>
              <Button 
                color="danger" 
                onClick={handleDelete}
                className="bg-gradient-to-r from-red-600 to-red-700"
              >
                Delete Forever
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );

  return (
    <div className="min-h-screen py-8 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="container px-6 mx-auto xl:px-20">
        {view === "list" ? renderListView() : renderFormView()}
        {renderDeleteModal()}
      </div>
    </div>
  );
};

export default AdminBlogManage;